import { useQueryClient } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useCompanies } from 'src/composables/companies/useCompanies'
import { idbService } from 'src/services/app/idb.service'
import { log } from 'src/services/app/log.service'
import { storageService } from 'src/services/app/storage.service'
import { STORAGE_KEYS } from 'src/services/consts/api.const'
import {
  filterCompanies,
  getInitialFilter,
  getInitialTableState,
  parseIntoQuery,
} from 'src/services/helpers/companies.helper'

export function useCompaniesQuery() {
  const router = useRouter()
  const route = useRoute()
  const queryClient = useQueryClient()
  const filterBy = ref(getInitialFilter(route.query))
  const tableState = ref(getInitialTableState(route.query))

  const { companies: companiesData, isLoading: isCompaniesLoading, error } = useCompanies()

  // Filtered and sorted companies
  const filteredCompanies = computed(() => {
    if (!companiesData.value?.length) return []
    return filterCompanies(companiesData.value, filterBy.value)
  })

  const isLoading = computed(() => isCompaniesLoading.value)

  const syncToUrl = () => {
    const newQuery = parseIntoQuery({ ...filterBy.value, ...tableState.value })

    // Determine if we should push (for page changes) or replace (for other changes)
    const oldPage = route.query.page ? parseInt(route.query.page, 10) : 1
    const newPage = tableState.value.page || 1
    const toPush = newPage !== oldPage

    if (toPush) {
      router.push({ query: newQuery })
    } else {
      router.replace({ query: newQuery })
    }
  }

  const setFilter = (filterByToUpdate) => {
    filterBy.value = {
      ...filterBy.value,
      ...filterByToUpdate,
    }

    // Reset page to 1 when filters change
    setTableState({ page: 1 })

    // Save to sessionStorage
    storageService.setSession(STORAGE_KEYS.LAST_FILTER_KEY, filterBy.value)

    // Sync to URL
    syncToUrl()
  }

  const setTableState = (tableStateToUpdate) => {
    tableState.value = {
      ...tableState.value,
      ...tableStateToUpdate,
    }

    // Save to sessionStorage
    storageService.setSession(STORAGE_KEYS.LAST_TABLE_STATE_KEY, tableState.value)

    // Sync to URL (push for page changes, replace for other changes)
    syncToUrl()
  }

  const reloadCompanies = async () => {
    try {
      // Clear IndexedDB cache
      await idbService.removeData(STORAGE_KEYS.COMPANIES)
      // Clear cache in query client
      await queryClient.invalidateQueries([STORAGE_KEYS.COMPANIES])

      log.info('Companies data reloaded successfully')
    } catch (error) {
      log.error('Failed to reload companies data', error)
    }
  }

  // Watch for URL changes (browser back/forward)
  watch(
    () => route.query,
    (newQuery, oldQuery) => {
      if (newQuery && JSON.stringify(newQuery) !== JSON.stringify(oldQuery)) {
        filterBy.value = getInitialFilter(newQuery, true)
        tableState.value = getInitialTableState(newQuery, true)
      }
    },
    { deep: true },
  )

  return {
    // Query state
    companies: companiesData,
    companiesToShow: filteredCompanies,
    filterBy,
    tableState,
    setFilter,
    setTableState,
    reloadCompanies,
    isLoading,
    isError: computed(() => !!error.value),
    error,
  }
}
